import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getAppointments, updateAppointmentStatus, deleteAppointment } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Trash2, Calendar, Clock, Mail, Phone, Building2 } from 'lucide-react';
import { format } from 'date-fns';

const statusColors = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  confirmed: 'bg-green-500/10 text-green-500 border-green-500/20',
  completed: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const res = await getAppointments();
      setAppointments(res.data);
    } catch (err) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAppointmentStatus(id, status);
      toast.success('Status updated');
      loadAppointments();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await deleteAppointment(id);
      toast.success('Appointment deleted');
      loadAppointments();
    } catch (err) {
      toast.error('Failed to delete appointment');
    }
  };

  const filteredAppointments = filter === 'all' 
    ? appointments 
    : appointments.filter(apt => apt.status === filter);

  return (
    <AdminLayout title="Appointments">
      <div className="space-y-6" data-testid="admin-appointments">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Appointments</h1>
            <p className="text-[#888] text-sm">Manage booking requests</p>
          </div>
          
          {/* Filter */}
          <div className="flex gap-2">
            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-xs font-medium capitalize transition-colors ${
                  filter === status
                    ? 'bg-[#ff5a1f] text-white'
                    : 'bg-[#121212] text-[#888] hover:text-white border border-[#333]'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : filteredAppointments.length > 0 ? (
          <div className="space-y-4">
            {filteredAppointments.map((apt) => (
              <div
                key={apt.id}
                className="bg-[#0A0A0A] border border-[#1F1F1F] p-6"
                data-testid={`appointment-${apt.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-syne font-bold text-white text-lg">{apt.name}</h3>
                      <span className={`px-3 py-1 text-xs font-medium border ${statusColors[apt.status]}`}>
                        {apt.status}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-[#888]">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {apt.email}
                      </span>
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {apt.phone}
                      </span>
                      {apt.company && (
                        <span className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {apt.company}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-2 text-[#ff5a1f]">
                        <Calendar className="w-4 h-4" />
                        {apt.date}
                      </span>
                      <span className="flex items-center gap-2 text-[#ff5a1f]">
                        <Clock className="w-4 h-4" />
                        {apt.time}
                      </span>
                      <span className="text-white bg-[#121212] px-3 py-1">
                        {apt.service}
                      </span>
                    </div>
                    
                    {apt.message && (
                      <p className="text-[#888] text-sm bg-[#121212] p-3 border-l-2 border-[#333]">
                        {apt.message}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:min-w-[200px]">
                    <select
                      value={apt.status}
                      onChange={(e) => handleStatusChange(apt.id, e.target.value)}
                      className="bg-[#121212] border border-[#333] text-white px-4 py-2 text-sm outline-none"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button
                      onClick={() => handleDelete(apt.id)}
                      className="border border-[#333] text-red-500 px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888]">
              {filter === 'all' ? 'No appointments yet' : `No ${filter} appointments`}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
